import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Badge } from "../ui/Badge";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";

type Category = {
  id: number;
  name: string;
  sort_order: number;
  product_count: number;
};

type ProductLite = {
  sku: string;
  name: string;
  category_id: number | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CategoriesAdmin({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"list" | "create" | "products">("list");
  const [selected, setSelected] = useState<Category | null>(null);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: cats, isLoading } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/api/categories")).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setMode("list");
    setSelected(null);
    setNewName("");
    setRenaming(null);
    setError(null);
  }, [open]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["categories"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
  };

  const createMut = useMutation({
    mutationFn: async () =>
      (await api.post("/api/categories", { name: newName.trim() })).data,
    onSuccess: () => {
      invalidate();
      setMode("list");
      setNewName("");
    },
    onError: (e) => setError(errMsg(e)),
  });

  const renameMut = useMutation({
    mutationFn: async (v: { id: number; name: string }) =>
      (await api.patch(`/api/categories/${v.id}`, { name: v.name })).data,
    onSuccess: () => {
      invalidate();
      setRenaming(null);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (v: { id: number; confirm: boolean }) =>
      (await api.delete(`/api/categories/${v.id}`, { params: { confirm: v.confirm } })).data,
    onSuccess: () => invalidate(),
    onError: (e) => setError(errMsg(e)),
  });

  const busy = createMut.isPending || renameMut.isPending || deleteMut.isPending;

  function onDelete(c: Category) {
    setError(null);
    if (c.product_count > 0) {
      const ok = window.confirm(
        `"${c.name}" contiene ${c.product_count} prodotti.\nEliminandola verranno scollegati (Senza categoria). Procedere?`,
      );
      if (!ok) return;
      deleteMut.mutate({ id: c.id, confirm: true });
    } else {
      deleteMut.mutate({ id: c.id, confirm: false });
    }
  }

  const title =
    mode === "create"
      ? "Nuova categoria"
      : mode === "products" && selected
        ? selected.name
        : "Categorie prodotto";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        mode === "create" ? (
          <>
            <Button variant="ghost" block onClick={() => setMode("list")} disabled={busy}>
              Annulla
            </Button>
            <Button
              variant="primary"
              block
              disabled={busy || newName.trim().length < 2}
              onClick={() => {
                setError(null);
                createMut.mutate();
              }}
            >
              Crea categoria
            </Button>
          </>
        ) : mode === "products" ? (
          <Button variant="ghost" block onClick={() => setMode("list")}>
            <Icon name="chev-r" size={14} /> Torna alle categorie
          </Button>
        ) : (
          <Button variant="primary" block onClick={() => setMode("create")}>
            <Icon name="plus" size={14} /> Nuova categoria
          </Button>
        )
      }
    >
      {error && (
        <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
          {error}
        </Banner>
      )}

      {mode === "create" && (
        <Field label="Nome categoria" hint="Minimo 2 caratteri · gli spazi doppi vengono normalizzati">
          <input
            className="tcg-input"
            value={newName}
            autoFocus
            placeholder="es. Display Pokemon JP"
            onChange={(e) => setNewName(e.target.value)}
          />
        </Field>
      )}

      {mode === "products" && selected && (
        <CategoryProducts category={selected} onChanged={invalidate} />
      )}

      {mode === "list" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {isLoading && <div style={{ color: "var(--tcg-muted)", fontSize: 13 }}>Caricamento…</div>}
          {cats?.map((c) => (
            <div
              key={c.id}
              style={{
                border: "1px solid var(--tcg-border)",
                borderRadius: 14,
                padding: "10px 12px",
                background: "var(--tcg-card)",
              }}
            >
              {renaming === c.id ? (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    className="tcg-input"
                    value={renameValue}
                    autoFocus
                    onChange={(e) => setRenameValue(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="primary"
                    size="small"
                    disabled={busy || renameValue.trim().length < 2}
                    onClick={() => {
                      setError(null);
                      renameMut.mutate({ id: c.id, name: renameValue.trim() });
                    }}
                  >
                    <Icon name="check" size={12} />
                  </Button>
                  <Button variant="ghost" size="small" onClick={() => setRenaming(null)}>
                    <Icon name="x" size={12} />
                  </Button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "var(--tcg-ink)" }}>{c.name}</div>
                  </div>
                  <Badge tone={c.product_count > 0 ? "navy" : "ghost"}>{c.product_count}</Badge>
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setSelected(c);
                      setMode("products");
                    }}
                  >
                    <Icon name="box" size={12} /> Prodotti
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      setRenaming(c.id);
                      setRenameValue(c.name);
                    }}
                  >
                    <Icon name="tag" size={12} />
                  </Button>
                  <Button variant="danger" size="small" disabled={busy} onClick={() => onDelete(c)}>
                    <Icon name="x" size={12} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function CategoryProducts({ category, onChanged }: { category: Category; onChanged: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data, isFetching } = useQuery<{ items: ProductLite[] }>({
    queryKey: ["cat-products", search],
    queryFn: async () =>
      (
        await api.get("/api/products", {
          params: { search: search || undefined, page_size: 100 },
        })
      ).data,
  });

  const assignMut = useMutation({
    mutationFn: async (v: { sku: string; action: "add" | "remove" }) =>
      (await api.post(`/api/categories/${category.id}/assign`, { skus: [v.sku], action: v.action })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cat-products"] });
      onChanged();
    },
    onError: (e) => setError(errMsg(e)),
  });

  const items = data?.items ?? [];

  return (
    <div>
      {error && (
        <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
          {error}
        </Banner>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--tcg-card)",
          border: "1.5px solid var(--tcg-border)",
          borderRadius: 12,
          padding: "9px 12px",
          marginBottom: 10,
        }}
      >
        <Icon name="search" size={14} />
        <input
          style={{ flex: 1, border: "none", outline: "none", fontSize: 13, fontWeight: 600, background: "transparent" }}
          placeholder="Cerca per SKU o nome…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isFetching && <span style={{ fontSize: 10, color: "var(--tcg-muted)" }}>···</span>}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.length === 0 && (
          <div style={{ color: "var(--tcg-muted)", fontSize: 12, textAlign: "center", padding: 12 }}>
            Nessun prodotto trovato.
          </div>
        )}
        {items.map((p) => {
          const inCat = p.category_id === category.id;
          return (
            <div
              key={p.sku}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid var(--tcg-border)",
                borderRadius: 12,
                padding: "8px 10px",
                background: inCat ? "var(--tcg-success-bg)" : "var(--tcg-card)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.name}
                </div>
                <div className="tcg-mono" style={{ fontSize: 9.5, color: "var(--tcg-muted)", marginTop: 1 }}>
                  {p.sku}
                </div>
              </div>
              <Button
                variant={inCat ? "danger" : "primary"}
                size="small"
                disabled={assignMut.isPending}
                onClick={() => {
                  setError(null);
                  assignMut.mutate({ sku: p.sku, action: inCat ? "remove" : "add" });
                }}
              >
                <Icon name={inCat ? "x" : "plus"} size={12} /> {inCat ? "Rimuovi" : "Aggiungi"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
